import type { DataImportStartImportMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response ({ message }) to the GraphQL mutation
 * response shape so consumers branching on the result do not need to know
 * which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  message: string;
}): DataImportStartImportMutation => ({
  dataImportStartImport: {
    __typename: 'DataImportStartImportOutput',
    message: data.message,
  },
});

/**
 * NOTE: the tRPC path is ASYNC — the mutation resolves as soon as the import
 * pipeline has been kicked off (status moves to 'initiatingImport' and the
 * results page's status subscription shows live progress), whereas the legacy
 * Hasura action ran synchronously. Consumers must not assume the import is
 * complete when the promise resolves.
 */
export const useDataImportStartImportTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.dataImport.startImport.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataImport.dataImports.queryKey(),
        });

        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataImport.dataImportById.queryKey(),
        });
      },
    }),
  });

  return {
    dataImportStartImport: async (
      id: string
    ): Promise<DataImportStartImportMutation> => {
      const result = await mutation.mutateAsync({ id });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
